import formatCustomDate from "./customDateFormat";

// ─── Export helpers ───────────────────────────────────────────────────────────
const exportTextDecoder =
  typeof TextDecoder !== "undefined"
    ? new TextDecoder("utf-8", { fatal: false })
    : null;

export function normalizeExportText(value) {
  const raw = String(value ?? "");
  if (!raw || !exportTextDecoder) return raw;

  const looksMojibake = /(?:Ã.|Â.|â.|à.|ðŸ)/.test(raw);
  if (!looksMojibake) return raw;

  try {
    const bytes = Uint8Array.from(
      Array.from(raw, (char) => char.charCodeAt(0) & 0xff),
    );
    const repaired = exportTextDecoder.decode(bytes);
    return repaired.includes("\uFFFD") ? raw : repaired;
  } catch (error) {
    return raw;
  }
}

export function escapeHtml(value) {
  return normalizeExportText(value)
    .split("&")
    .join("&amp;")
    .split("<")
    .join("&lt;")
    .split(">")
    .join("&gt;");
}

export function formatCurrency(value) {
  return `₹${(parseFloat(value) || 0).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

export function formatCurrencyTamil(value) {
  return `ரூ ${Number(parseFloat(value) || 0).toLocaleString("ta-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

export function normalizeDateValue(value) {
  if (value === null || value === undefined || value === "") return null;
  if (typeof value === "string") {
    const trimmed = value.trim();
    return trimmed.replace(
      /^(\d{4}-\d{2}-\d{2})[ ](\d{2}:\d{2}(?::\d{2})?)$/,
      "$1T$2",
    );
  }
  return value;
}

export function parseIndiaDate(value) {
  if (!value) return null;
  if (value instanceof Date) return value;

  const raw = String(value).trim();
  const normalized = raw.replace(/ /g, "T");
  if (/Z|[+-]\d{2}:?\d{2}$/.test(normalized)) {
    const parsed = new Date(normalized);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }

  const match = normalized.match(
    /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2}))?$/,
  );
  if (!match) {
    const parsed = new Date(normalized);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }

  const [, year, month, day, hour, minute, second = "00"] = match;
  const utcMs =
    Date.UTC(
      Number(year),
      Number(month) - 1,
      Number(day),
      Number(hour),
      Number(minute),
      Number(second),
    ) - 5.5 * 60 * 60 * 1000;
  return new Date(utcMs);
}

export function formatIndiaDateTime(value, locale, options) {
  const date = parseIndiaDate(value);
  if (!date || Number.isNaN(date.getTime())) return String(value);

  return new Intl.DateTimeFormat(locale, {
    timeZone: "Asia/Kolkata",
    ...options,
  }).format(date);
}

export function formatEntryDateTime(value) {
  if (!value) return "—";

  return formatIndiaDateTime(value, "en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });
}

export function formatEntryDateTimeTamil(value) {
  if (!value) return "இல்லை";

  return formatIndiaDateTime(value, "ta-IN", {
    day: "2-digit",
    month: "long",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });
}

export function isThaaiMamanEntry(entry) {
  return (
    entry?.thaai_maman === true ||
    entry?.thaai_maman === 1 ||
    entry?.thaai_maman === "1"
  );
}

export function isEditedEntry(entry) {
  return (
    entry?.is_edited === true ||
    entry?.is_edited === 1 ||
    entry?.is_edited === "1"
  );
}

export function getEditedStatusLabel(entry) {
  return isEditedEntry(entry) ? "Edited Entry" : "Original Entry";
}

export function getTamilAssetTypeLabel(assetType) {
  switch (assetType) {
    case "cash":
      return "பணம்";
    case "online":
      return "ஆன்லைன்";
    case "ornaments":
      return "நகை";
    case "things":
      return "பொருள்";
    default:
      return assetType || "இல்லை";
  }
}

export function getScreenEntryAmount(entry) {
  const raw = entry?.amount;
  if (raw === null || raw === undefined || raw === "") return "-";
  const parsed = parseFloat(raw);
  if (Number.isNaN(parsed)) return "-";
  return formatCurrency(parsed);
}

export function getExportEntryAmount(entry) {
  const raw = entry?.amount;
  if (raw === null || raw === undefined || raw === "") return "இல்லை";
  const parsed = parseFloat(raw);
  if (Number.isNaN(parsed)) return "இல்லை";
  return formatCurrencyTamil(parsed);
}

export function sortEntriesForExport(entries) {
  return [...entries]
    .map((entry, index) => ({ entry, index }))
    .sort((a, b) => {
      const thaiDiff =
        Number(isThaaiMamanEntry(b.entry)) - Number(isThaaiMamanEntry(a.entry));
      if (thaiDiff !== 0) return thaiDiff;

      if (isThaaiMamanEntry(a.entry)) {
        const orderA = a.entry.thaai_maman_order !== null && a.entry.thaai_maman_order !== undefined ? Number(a.entry.thaai_maman_order) : Infinity;
        const orderB = b.entry.thaai_maman_order !== null && b.entry.thaai_maman_order !== undefined ? Number(b.entry.thaai_maman_order) : Infinity;
        if (orderA !== orderB) return orderA - orderB;
      }

      return a.index - b.index;
    })
    .map(({ entry }) => entry);
}

export function parseDenominationBreakdown(entry) {
  const breakdown = entry?.denomination_breakdown;
  if (breakdown && typeof breakdown === "object" && !Array.isArray(breakdown)) {
    return breakdown;
  }

  if (
    typeof entry?.denomination_amount === "string" &&
    entry.denomination_amount.trim()
  ) {
    try {
      const parsed = JSON.parse(entry.denomination_amount);
      if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
        return parsed;
      }
    } catch (error) {
      return null;
    }
  }

  return null;
}

export function getEntryDenominationTotal(entry) {
  const denominationBreakdown = parseDenominationBreakdown(entry);
  if (!denominationBreakdown) return null;

  const total = Object.entries(denominationBreakdown).reduce(
    (sum, [note, count]) =>
      sum + (parseInt(note, 10) || 0) * (parseInt(count, 10) || 0),
    0,
  );

  return total > 0 ? total : null;
}

export function getEntryDenominationSummary(entry) {
  const denominationBreakdown = parseDenominationBreakdown(entry);
  if (!denominationBreakdown) return "-";

  const parts = Object.entries(denominationBreakdown)
    .map(([note, count]) => ({
      note: parseInt(note, 10) || 0,
      count: parseInt(count, 10) || 0,
    }))
    .filter(({ note, count }) => note > 0 && count > 0)
    .sort((a, b) => b.note - a.note)
    .map(({ note, count }) => `${note} x ${count}`);

  return parts.length ? parts.join(", ") : "-";
}

export function getEntryReturnAmount(entry) {
  const denominationTotal = getEntryDenominationTotal(entry);
  if (denominationTotal === null) return null;

  const amountValue = parseFloat(entry?.amount) || 0;
  const difference = denominationTotal - amountValue;

  return difference > 0 ? difference : 0;
}

export function getOverallDenominationCounts(entries) {
  return entries.reduce((acc, entry) => {
    const denominationBreakdown = parseDenominationBreakdown(entry);
    if (!denominationBreakdown) return acc;

    Object.entries(denominationBreakdown).forEach(([note, count]) => {
      const parsedNote = parseInt(note, 10) || 0;
      const parsedCount = parseInt(count, 10) || 0;
      if (parsedNote <= 0 || parsedCount <= 0) return;
      acc[parsedNote] = (acc[parsedNote] || 0) + parsedCount;
    });

    return acc;
  }, {});
}

export function getOverallReturnAmount(entries) {
  return entries.reduce((sum, entry) => {
    const returnAmount = getEntryReturnAmount(entry);
    return sum + (returnAmount || 0);
  }, 0);
}

export function buildDisclaimerHtmlTamil() {
  return `
    <div class="disclaimer">
      <strong>எச்சரிக்கை:</strong> இந்த ஆவணத்தில் உள்ள விழா மற்றும் மொய் தகவல்கள் ரகசியமானவை.
      இதை வெளியிட்டால் கடுமையான நடவடிக்கை எடுக்கப்படும்.
    </div>
  `;
}

export function resolveVendorPhone(eventInfo, vendorName) {
  const directPhone = eventInfo?.vendor_mobile || eventInfo?.vendorPhone;
  if (directPhone) return directPhone;

  return "இல்லை";
}

export function buildVendorInfoHtmlTamil(eventInfo) {
  const vendorName =
    eventInfo?.vendor_name || eventInfo?.vendorName || "Digital Moi Tech";
  const vendorPhone = resolveVendorPhone(eventInfo, vendorName);
  const vendorAddress =
    eventInfo?.vendor_address || eventInfo?.vendorAddress || "இல்லை";
  const customerName = eventInfo?.customer_name || "இல்லை";
  const eventType = eventInfo?.event_type || "இல்லை";
  const eventDate = formatCustomDate(eventInfo?.event_date, "date") || "இல்லை";
  const eventAddress = eventInfo?.event_address || "இல்லை";

  return `
    <div class="section">
      <div class="section-title">விசேஷ வீட்டாளர் விவரம்</div>
      <table class="meta-table">
        <tr><td>விசேஷ வீட்டாளர் பெயர்</td><td>${escapeHtml(vendorName)}</td></tr>
        <tr><td>முகவரி</td><td>${escapeHtml(vendorAddress)}</td></tr>
        <tr><td>தொலைபேசி</td><td>${escapeHtml(vendorPhone)}</td></tr>
      </table>
    </div>

    <div class="section">
      <div class="section-title">விழா விவரம்</div>
      <table class="meta-table">
        <tr><td>வாடிக்கையாளர் பெயர்</td><td>${escapeHtml(customerName)}</td></tr>
        <tr><td>விழா வகை</td><td>${escapeHtml(eventType)}</td></tr>
        <tr><td>விழா தேதி</td><td>${escapeHtml(eventDate)}</td></tr>
        <tr><td>விழா முகவரி</td><td>${escapeHtml(eventAddress)}</td></tr>
      </table>
    </div>
  `;
}

export function buildTallyHtmlTamil(stats) {
  return `
    <div class="section">
      <div class="section-title">மொத்த சுருக்கம்</div>
      <div class="stats-grid">
        <div class="stat-card">
          <div class="stat-label">மொத்த பதிவுகள்</div>
          <div class="stat-value">${Number(stats.totalEntries).toLocaleString("ta-IN")}</div>
        </div>
        <div class="stat-card">
          <div class="stat-label">பண மொத்தம்</div>
          <div class="stat-value">${formatCurrencyTamil(stats.totalCash)}</div>
        </div>
        <div class="stat-card">
          <div class="stat-label">ஆன்லைன் மொத்தம்</div>
          <div class="stat-value">${formatCurrencyTamil(stats.totalOnline)}</div>
        </div>
        <div class="stat-card">
          <div class="stat-label">பணம் மொத்தம்</div>
          <div class="stat-value">${formatCurrencyTamil(stats.totalMonetary)}</div>
        </div>
        <div class="stat-card">
          <div class="stat-label">நகை எண்ணிக்கை</div>
          <div class="stat-value">${Number(stats.ornamentsCount).toLocaleString("ta-IN")}</div>
        </div>
        <div class="stat-card">
          <div class="stat-label">பொருள் எண்ணிக்கை</div>
          <div class="stat-value">${Number(stats.thingsCount).toLocaleString("ta-IN")}</div>
        </div>
      </div>
    </div>
  `;
}

export function buildThaaiMamanSummaryHtmlTamil(stats) {
  return `
    <div class="section">
      <div class="section-title">தாய்மாமன் சிறப்பு சுருக்கம்</div>
      <div class="stats-grid">
        <div class="stat-card">
          <div class="stat-label">தாய்மாமன் பதிவுகள்</div>
          <div class="stat-value">${Number(stats.thaaiMamanCount || 0).toLocaleString("ta-IN")}</div>
        </div>
        <div class="stat-card">
          <div class="stat-label">தாய்மாமன் மொத்தம்</div>
          <div class="stat-value">${formatCurrencyTamil(stats.thaaiMamanTotal || 0)}</div>
        </div>
      </div>
    </div>
  `;
}

export function buildEntryTableRowsTamil(entries) {
  return entries
    .map(
      (entry, index) => `
        <tr>
          <td>${Number(index + 1).toLocaleString("ta-IN")}</td>
          <td>${escapeHtml(entry.name || "இல்லை")}</td>
          <td>${escapeHtml(entry.family_name || "இல்லை")}</td>
          <td>${escapeHtml(maskMobileNumber(entry.mobile))}</td>
          <td>${escapeHtml(entry.village || "இல்லை")}</td>
          <td>${escapeHtml(entry.city || "இல்லை")}</td>
          <td>${escapeHtml(entry.state || "இல்லை")}</td>
          <td>${escapeHtml(getTamilAssetTypeLabel(entry.asset_type))}</td>
          <td>${getExportEntryAmount(entry)}</td>
          <td>${getEntryDenominationTotal(entry) !== null ? formatCurrencyTamil(getEntryDenominationTotal(entry)) : "இல்லை"}</td>
          <td>${escapeHtml(getEntryDenominationSummary(entry) === "-" ? "இல்லை" : getEntryDenominationSummary(entry))}</td>
          <td>${getEntryReturnAmount(entry) !== null ? formatCurrencyTamil(getEntryReturnAmount(entry)) : "இல்லை"}</td>
          <td>${escapeHtml(getEditedStatusLabel(entry))}</td>
          <td>${escapeHtml(entry.asset || "இல்லை")}</td>
          <td>${escapeHtml(entry.token || "இல்லை")}</td>
          <td>${escapeHtml(isThaaiMamanEntry(entry) ? "ஆம்" : "இல்லை")}</td>
          <td>${escapeHtml(formatEntryDateTimeTamil(entry.datetime || entry.created_at))}</td>
        </tr>
      `,
    )
    .join("");
}

export function buildEntriesTableSectionTamil(title, entries, emptyMessage) {
  const rows = buildEntryTableRowsTamil(entries);

  return `
    <div class="section">
      <div class="section-title">${title}</div>
      <table class="entries-table">
        <thead>
          <tr>
            <th>#</th>
            <th>பெயர்</th>
            <th>குடும்பம்</th>
            <th>மொபைல்</th>
            <th>ஊர்</th>
            <th>நகர்</th>
            <th>மாநிலம்</th>
            <th>வகை</th>
            <th>தொகை</th>
            <th>நோட்டு மொத்தம்</th>
            <th>நோட்டு விவரம்</th>
            <th>மீதி</th>
            <th>Status</th>
            <th>பொருள்</th>
            <th>டோக்கன்</th>
            <th>தாய்மாமன்</th>
            <th>தேதி மற்றும் நேரம்</th>
          </tr>
        </thead>
        <tbody>${rows || `<tr><td colspan="17">${emptyMessage}</td></tr>`}</tbody>
      </table>
    </div>
  `;
}

export function buildThaaiMamanEntriesTableHtmlTamil(entries) {
  const thaiEntries = sortEntriesForExport(entries).filter((entry) =>
    isThaaiMamanEntry(entry),
  );

  return buildEntriesTableSectionTamil(
    "தாய்மாமன் பதிவுகள்",
    thaiEntries,
    "தாய்மாமன் பதிவுகள் இல்லை.",
  );
}

export function buildEntriesTableHtmlTamil(entries) {
  return buildEntriesTableSectionTamil(
    "அனைத்து பதிவுகள்",
    sortEntriesForExport(entries),
    "பதிவுகள் இல்லை.",
  );
}

export function buildOverallDenominationHtmlTamil(entries) {
  const totals = getOverallDenominationCounts(entries);
  const totalReturnAmount = getOverallReturnAmount(entries);
  const rows = Object.entries(totals)
    .map(([note, count]) => ({
      note: parseInt(note, 10) || 0,
      count: parseInt(count, 10) || 0,
    }))
    .filter(({ note, count }) => note > 0 && count > 0)
    .sort((a, b) => b.note - a.note)
    .map(
      ({ note, count }) => `
        <tr>
          <td>${formatCurrencyTamil(note)}</td>
          <td>${Number(count).toLocaleString("ta-IN")}</td>
          <td>${formatCurrencyTamil(note * count)}</td>
        </tr>
      `,
    )
    .join("");

  if (!rows) {
    return `
      <div class="section">
        <div class="section-title">மொத்த நோட்டு விவரம்</div>
        <p>நோட்டு விவரம் இல்லை.</p>
      </div>
    `;
  }

  return `
    <div class="section">
      <div class="section-title">மொத்த நோட்டு விவரம்</div>
      <table class="entries-table">
        <thead>
          <tr>
            <th>நோட்டு</th>
            <th>எண்ணிக்கை</th>
            <th>மொத்தம்</th>
          </tr>
        </thead>
        <tbody>${rows}</tbody>
        <tfoot>
          <tr>
            <td colspan="2"><strong>மீதி மொத்தம்</strong></td>
            <td><strong>${formatCurrencyTamil(totalReturnAmount)}</strong></td>
          </tr>
        </tfoot>
      </table>
    </div>
  `;
}

export function buildPdfDocumentHtmlTamil({ eventInfo, entries, stats }) {
  const generatedAt = formatEntryDateTimeTamil(new Date());

  return `
    <!DOCTYPE html>
    <html lang="ta">
      <head>
        <meta charset="UTF-8" />
        <title>மொய் பதிவுகள்</title>
        <style>
          * { box-sizing: border-box; }
          body { font-family: "Noto Sans Tamil", "Latha", "Vijaya", Arial, sans-serif; margin: 0; color: #111827; background: #ffffff; }
          .page { padding: 24px; }
          .page-break { page-break-before: always; }
          .header { border-bottom: 3px solid #111827; padding-bottom: 12px; margin-bottom: 20px; }
          .header h1 { margin: 0 0 6px; font-size: 24px; }
          .header p { margin: 0; color: #4b5563; font-size: 12px; }
          .section { margin-bottom: 20px; }
          .section-title { font-size: 15px; font-weight: 700; margin-bottom: 10px; color: #111827; }
          .meta-table, .entries-table { width: 100%; border-collapse: collapse; }
          .meta-table td, .entries-table th, .entries-table td { border: 1px solid #d1d5db; padding: 8px; font-size: 12px; vertical-align: top; }
          .meta-table td:first-child { width: 32%; font-weight: 700; background: #f9fafb; }
          .entries-table thead th { background: #111827; color: #ffffff; font-size: 11px; text-align: left; }
          .entries-table tbody tr:nth-child(even) { background: #f9fafb; }
          .stats-grid { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 12px; }
          .stat-card { border: 1px solid #d1d5db; border-radius: 10px; padding: 12px; background: #f9fafb; }
          .stat-label { font-size: 11px; color: #6b7280; margin-bottom: 6px; }
          .stat-value { font-size: 18px; font-weight: 700; color: #111827; }
          .disclaimer { margin-top: 16px; padding: 14px 16px; border: 2px solid #dc2626; background: #fef2f2; color: #991b1b; font-size: 12px; line-height: 1.5; border-radius: 10px; }
        </style>
      </head>
      <body>
        <div class="page">
          <div class="header">
            <h1>மொய் பதிவு அறிக்கை</h1>
            <p>உருவாக்கப்பட்ட நேரம்: ${escapeHtml(generatedAt)}</p>
          </div>
          ${buildVendorInfoHtmlTamil(eventInfo)}
          ${buildThaaiMamanSummaryHtmlTamil(stats)}
          ${buildTallyHtmlTamil(stats)}
          ${buildDisclaimerHtmlTamil()}
        </div>

        <div class="page page-break">
          <div class="header">
            <h1>மொய் பதிவு விரிவான பட்டியல்</h1>
            <p>மொத்த பதிவுகள்: ${Number(entries.length).toLocaleString("ta-IN")}</p>
          </div>
          ${buildThaaiMamanEntriesTableHtmlTamil(entries)}
          ${buildEntriesTableHtmlTamil(entries)}
          ${buildOverallDenominationHtmlTamil(entries)}
          ${buildDisclaimerHtmlTamil()}
        </div>
      </body>
    </html>
  `;
}

export function buildExcelWorksheetHtmlTamil({ eventInfo, entries, stats }) {
  const vendorName =
    eventInfo?.vendor_name || eventInfo?.vendorName || "Digital Moi Tech";
  const vendorPhone = resolveVendorPhone(eventInfo, vendorName);
  const vendorAddress =
    eventInfo?.vendor_address || eventInfo?.vendorAddress || "இல்லை";
  const customerName = eventInfo?.customer_name || "இல்லை";
  const eventType = eventInfo?.event_type || "இல்லை";
  const eventDate = formatCustomDate(eventInfo?.event_date, "date") || "இல்லை";
  const eventAddress = eventInfo?.event_address || "இல்லை";
  const sortedEntries = sortEntriesForExport(entries);

  const entryRows = sortedEntries
    .map(
      (entry, index) => `
        <tr>
          <td>${Number(index + 1).toLocaleString("ta-IN")}</td>
          <td>${escapeHtml(entry.name || "இல்லை")}</td>
          <td>${escapeHtml(entry.family_name || "இல்லை")}</td>
          <td>${escapeHtml(maskMobileNumber(entry.mobile))}</td>
          <td>${escapeHtml(entry.village || "இல்லை")}</td>
          <td>${escapeHtml(entry.city || "இல்லை")}</td>
          <td>${escapeHtml(entry.state || "இல்லை")}</td>
          <td>${escapeHtml(getTamilAssetTypeLabel(entry.asset_type))}</td>
          <td>${escapeHtml(getExportEntryAmount(entry))}</td>
          <td>${escapeHtml(getEntryDenominationTotal(entry) !== null ? formatCurrencyTamil(getEntryDenominationTotal(entry)) : "இல்லை")}</td>
          <td>${escapeHtml(getEntryDenominationSummary(entry) === "-" ? "இல்லை" : getEntryDenominationSummary(entry))}</td>
          <td>${escapeHtml(getEntryReturnAmount(entry) !== null ? formatCurrencyTamil(getEntryReturnAmount(entry)) : "இல்லை")}</td>
          <td>${escapeHtml(getEditedStatusLabel(entry))}</td>
          <td>${escapeHtml(entry.asset || "இல்லை")}</td>
          <td>${escapeHtml(entry.token || "இல்லை")}</td>
          <td>${escapeHtml(isThaaiMamanEntry(entry) ? "ஆம்" : "இல்லை")}</td>
          <td>${escapeHtml(formatEntryDateTimeTamil(entry.datetime || entry.created_at))}</td>
        </tr>
      `,
    )
    .join("");

  const denominationRows =
    Object.entries(getOverallDenominationCounts(entries))
      .map(([note, count]) => {
        const parsedNote = parseInt(note, 10) || 0;
        const parsedCount = parseInt(count, 10) || 0;
        return parsedNote > 0 && parsedCount > 0
          ? `<tr><td>${escapeHtml(formatCurrencyTamil(parsedNote))}</td><td>${escapeHtml(Number(parsedCount).toLocaleString("ta-IN"))}</td><td>${escapeHtml(formatCurrencyTamil(parsedNote * parsedCount))}</td><td colspan="14"></td></tr>`
          : "";
      })
      .join("") || '<tr><td colspan="17">நோட்டு விவரம் இல்லை.</td></tr>';

  return `
    <html lang="ta" xmlns:o="urn:schemas-microsoft-com:office:office"
          xmlns:x="urn:schemas-microsoft-com:office:excel"
          xmlns="http://www.w3.org/TR/REC-html40">
      <head>
        <meta charset="UTF-8" />
        <meta http-equiv="Content-Type" content="text/html; charset=utf-8" />
        <!--[if gte mso 9]><xml><x:ExcelWorkbook><x:ExcelWorksheets><x:ExcelWorksheet><x:Name>மொய் பதிவுகள்</x:Name><x:WorksheetOptions><x:DisplayGridlines/></x:WorksheetOptions></x:ExcelWorksheet></x:ExcelWorksheets></x:ExcelWorkbook></xml><![endif]-->
        <style>
          body { font-family: "Noto Sans Tamil", "Latha", "Vijaya", Arial, sans-serif; }
          table { border-collapse: collapse; width: 100%; }
          td, th { border: 1px solid #000; padding: 6px; }
          .title { font-size: 18px; font-weight: bold; background: #dbeafe; }
          .section { font-weight: bold; background: #e5e7eb; }
          .label { font-weight: bold; background: #f3f4f6; width: 220px; }
          .warning { color: #991b1b; background: #fee2e2; font-weight: bold; }
          .thead th { background: #111827; color: #fff; }
        </style>
      </head>
      <body>
        <table>
          <tr><td colspan="16" class="title">மொய் பதிவு ஏற்றுமதி</td></tr>
          <tr><td colspan="16">உருவாக்கப்பட்ட நேரம்: ${escapeHtml(formatEntryDateTimeTamil(new Date()))}</td></tr>

          <tr><td colspan="16" class="section">விசேஷ வீட்டாளர் விவரம்</td></tr>
          <tr><td class="label">விசேஷ வீட்டாளர் பெயர்</td><td colspan="15">${escapeHtml(vendorName)}</td></tr>
          <tr><td class="label">முகவரி</td><td colspan="15">${escapeHtml(vendorAddress)}</td></tr>
          <tr><td class="label">தொலைபேசி</td><td colspan="15">${escapeHtml(vendorPhone)}</td></tr>

          <tr><td colspan="16" class="section">விழா விவரம்</td></tr>
          <tr><td class="label">வாடிக்கையாளர் பெயர்</td><td colspan="15">${escapeHtml(customerName)}</td></tr>
          <tr><td class="label">விழா வகை</td><td colspan="15">${escapeHtml(eventType)}</td></tr>
          <tr><td class="label">விழா தேதி</td><td colspan="15">${escapeHtml(eventDate)}</td></tr>
          <tr><td class="label">விழா முகவரி</td><td colspan="15">${escapeHtml(eventAddress)}</td></tr>

          <tr><td colspan="16" class="section">தாய்மாமன் சிறப்பு சுருக்கம்</td></tr>
          <tr><td class="label">தாய்மாமன் பதிவுகள்</td><td colspan="15">${Number(stats.thaaiMamanCount || 0).toLocaleString("ta-IN")}</td></tr>
          <tr><td class="label">தாய்மாமன் மொத்தம்</td><td colspan="15">${escapeHtml(formatCurrencyTamil(stats.thaaiMamanTotal || 0))}</td></tr>

          <tr><td colspan="16" class="section">மொத்த சுருக்கம்</td></tr>
          <tr><td class="label">மொத்த பதிவுகள்</td><td colspan="15">${Number(stats.totalEntries).toLocaleString("ta-IN")}</td></tr>
          <tr><td class="label">பண மொத்தம்</td><td colspan="15">${escapeHtml(formatCurrencyTamil(stats.totalCash))}</td></tr>
          <tr><td class="label">ஆன்லைன் மொத்தம்</td><td colspan="15">${escapeHtml(formatCurrencyTamil(stats.totalOnline))}</td></tr>
          <tr><td class="label">பணம் மொத்தம்</td><td colspan="15">${escapeHtml(formatCurrencyTamil(stats.totalMonetary))}</td></tr>
          <tr><td class="label">நகை எண்ணிக்கை</td><td colspan="15">${Number(stats.ornamentsCount).toLocaleString("ta-IN")}</td></tr>
          <tr><td class="label">பொருள் எண்ணிக்கை</td><td colspan="15">${Number(stats.thingsCount).toLocaleString("ta-IN")}</td></tr>
          <tr><td colspan="16" class="warning">எச்சரிக்கை: இந்த ஆவணத்தில் உள்ள விழா மற்றும் மொய் தகவல்கள் ரகசியமானவை. இதை வெளியிட்டால் கடுமையான நடவடிக்கை எடுக்கப்படும்.</td></tr>

          <tr><td colspan="16" class="section">தாய்மாமன் பதிவுகள்</td></tr>
          <tr class="thead">
            <th>#</th>
            <th>பெயர்</th>
            <th>குடும்பம்</th>
            <th>மொபைல்</th>
            <th>ஊர்</th>
            <th>நகர்</th>
            <th>மாநிலம்</th>
            <th>வகை</th>
            <th>தொகை</th>
            <th>நோட்டு மொத்தம்</th>
            <th>நோட்டு விவரம்</th>
            <th>மீதி</th>
            <th>Status</th>
            <th>பொருள்</th>
            <th>டோக்கன்</th>
            <th>தாய்மாமன்</th>
            <th>தேதி மற்றும் நேரம்</th>
          </tr>
          ${buildEntryTableRowsTamil(sortedEntries.filter((entry) => isThaaiMamanEntry(entry))) || '<tr><td colspan="17">தாய்மாமன் பதிவுகள் இல்லை.</td></tr>'}

          <tr><td colspan="16" class="section">அனைத்து பதிவுகள்</td></tr>
          <tr class="thead">
            <th>#</th>
            <th>பெயர்</th>
            <th>குடும்பம்</th>
            <th>மொபைல்</th>
            <th>ஊர்</th>
            <th>நகர்</th>
            <th>மாநிலம்</th>
            <th>வகை</th>
            <th>தொகை</th>
            <th>நோட்டு மொத்தம்</th>
            <th>நோட்டு விவரம்</th>
            <th>மீதி</th>
            <th>Status</th>
            <th>பொருள்</th>
            <th>டோக்கன்</th>
            <th>தாய்மாமன்</th>
            <th>தேதி மற்றும் நேரம்</th>
          </tr>
          ${entryRows || '<tr><td colspan="17">பதிவுகள் இல்லை.</td></tr>'}
          <tr><td colspan="16" class="section">மொத்த நோட்டு விவரம்</td></tr>
          <tr class="thead"><th>நோட்டு</th><th>எண்ணிக்கை</th><th>மொத்தம்</th><th colspan="13"></th></tr>
          ${denominationRows}
          <tr><td colspan="2"><strong>மீதி மொத்தம்</strong></td><td><strong>${escapeHtml(formatCurrencyTamil(getOverallReturnAmount(entries)))}</strong></td><td colspan="13"></td></tr>
          <tr><td colspan="16" class="warning">எச்சரிக்கை: இந்த ஆவணத்தில் உள்ள விழா மற்றும் மொய் தகவல்கள் ரகசியமானவை. இதை வெளியிட்டால் கடுமையான நடவடிக்கை எடுக்கப்படும்.</td></tr>
        </table>
      </body>
    </html>
  `;
}

export function maskMobileNumber(mobile) {
  const digits = String(mobile ?? "").replace(/\D/g, "");
  if (!digits) return "இல்லை";
  if (digits.length <= 4) return digits;
  return `${"*".repeat(digits.length - 4)}${digits.slice(-4)}`;
}

export function downloadFile(content, fileName, mimeType) {
  const blob = new Blob([content], { type: mimeType });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
}

export function buildExportStats(entries) {
  const cashEntries = entries.filter((e) => e.asset_type === "cash");
  const onlineEntries = entries.filter((e) => e.asset_type === "online");
  const ornamentEntries = entries.filter((e) => e.asset_type === "ornaments");
  const thingEntries = entries.filter((e) => e.asset_type === "things");
  const thaaiMamanEntries = entries.filter((e) => isThaaiMamanEntry(e));

  const totalCash = cashEntries.reduce(
    (sum, entry) => sum + (parseFloat(entry.amount) || 0),
    0,
  );
  const totalOnline = onlineEntries.reduce(
    (sum, entry) => sum + (parseFloat(entry.amount) || 0),
    0,
  );
  const totalMonetary = totalCash + totalOnline;
  const thaaiMamanTotal = thaaiMamanEntries.reduce(
    (sum, entry) => sum + (parseFloat(entry.amount) || 0),
    0,
  );

  return {
    totalEntries: entries.length,
    totalCash,
    totalOnline,
    totalMonetary,
    thaaiMamanCount: thaaiMamanEntries.length,
    thaaiMamanTotal,
    ornamentsCount: ornamentEntries.length,
    thingsCount: thingEntries.length,
    giftAssetCount: ornamentEntries.length + thingEntries.length,
  };
}
